/**
 * Parse Pure LLM `<context-suggest>` tags and normalize metadata payloads for the
 * Link / Dismiss card UI. IDs stay in state for accept/decline goals but must not
 * be rendered.
 */

const SUGGEST_TAG_RE = /<context-suggest\b([^>]*)\/?\s*>/gi;
const TYPE_ATTR_RE = /\btype\s*=\s*["']([^"']+)["']/i;
const ID_ATTR_RE = /\bid\s*=\s*["']([^"']+)["']/i;
const TITLE_ATTR_RE = /\btitle\s*=\s*["']([^"']+)["']/i;

export const MAX_CONTEXT_SUGGESTS = 4;

/**
 * @param {unknown} type
 * @returns {'fintel'|'chart'|null}
 */
export function normalizeContextSuggestType(type) {
    const t = String(type || '').trim().toLowerCase();
    if (t === 'fintel' || t === 'product' || t === 'report') {
        return 'fintel';
    }
    if (t === 'chart' || t === 'whiteboard') {
        return 'chart';
    }
    return null;
}

/**
 * @param {unknown} raw
 * @returns {{type: 'fintel'|'chart', id: string, title: string|null}|null}
 */
export function normalizeContextSuggestion(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const id = String(raw.id || raw.targetId || '').trim();
    if (!id) {
        return null;
    }
    const type = normalizeContextSuggestType(raw.type);
    if (!type) {
        return null;
    }
    const titleRaw = raw.title != null ? String(raw.title).trim() : '';
    return {
        type,
        id,
        title: titleRaw || null,
    };
}

/**
 * @param {unknown} list
 * @returns {Array<{type: 'fintel'|'chart', id: string, title: string|null}>}
 */
export function normalizeContextSuggestions(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return [];
    }
    const seen = new Set();
    const out = [];
    for (const item of list) {
        if (out.length >= MAX_CONTEXT_SUGGESTS) {
            break;
        }
        const suggestion = normalizeContextSuggestion(item);
        if (!suggestion) {
            continue;
        }
        const key = `${suggestion.type}:${suggestion.id.toLowerCase()}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        out.push(suggestion);
    }
    return out;
}

/**
 * @param {string|null|undefined} text
 * @returns {Array<{type: 'fintel'|'chart', id: string, title: string|null}>}
 */
export function parseContextSuggestionsFromText(text) {
    if (text == null || typeof text !== 'string' || !text.includes('context-suggest')) {
        return [];
    }
    const seen = new Set();
    const out = [];
    SUGGEST_TAG_RE.lastIndex = 0;
    let match = SUGGEST_TAG_RE.exec(text);
    while (match && out.length < MAX_CONTEXT_SUGGESTS) {
        const attrs = match[1] || '';
        const idMatch = ID_ATTR_RE.exec(attrs);
        ID_ATTR_RE.lastIndex = 0;
        if (!idMatch) {
            match = SUGGEST_TAG_RE.exec(text);
            continue;
        }
        const id = idMatch[1].trim();
        if (!id) {
            match = SUGGEST_TAG_RE.exec(text);
            continue;
        }
        const typeMatch = TYPE_ATTR_RE.exec(attrs);
        TYPE_ATTR_RE.lastIndex = 0;
        const type = normalizeContextSuggestType(typeMatch ? typeMatch[1] : null);
        if (!type) {
            match = SUGGEST_TAG_RE.exec(text);
            continue;
        }
        const titleMatch = TITLE_ATTR_RE.exec(attrs);
        TITLE_ATTR_RE.lastIndex = 0;
        const title = titleMatch ? titleMatch[1].trim() : '';
        const key = `${type}:${id.toLowerCase()}`;
        if (!seen.has(key)) {
            seen.add(key);
            out.push({type, id, title: title || null});
        }
        match = SUGGEST_TAG_RE.exec(text);
    }
    return out;
}

/**
 * Prefer metadata (survives display strip); fall back to raw tag parse.
 * @param {{metadata?: object, rawText?: string}|null|undefined} source
 * @returns {Array<{type: 'fintel'|'chart', id: string, title: string|null}>}
 */
export function resolveContextSuggestions(source = {}) {
    const fromMeta = normalizeContextSuggestions(source?.metadata?.contextSuggestions);
    if (fromMeta.length > 0) {
        return fromMeta;
    }
    return parseContextSuggestionsFromText(source?.rawText);
}

/**
 * Goals that hit PureLlmContextExpansion ACCEPT / DECLINE on the server.
 * @param {{type: string, id: string, title?: string|null}} suggestion
 * @returns {string}
 */
export function buildContextSuggestLinkGoal(suggestion) {
    const title = suggestion?.title?.trim() || suggestion?.id || 'it';
    return `yes, add ${title}`;
}

/**
 * @param {{type: string, id: string, title?: string|null}} suggestion
 * @returns {string}
 */
export function buildContextSuggestDismissGoal(suggestion) {
    const title = suggestion?.title?.trim() || suggestion?.id || 'it';
    return `don't add ${title}`;
}

/**
 * @param {{type: string, id: string}} suggestion
 * @returns {string}
 */
export function contextSuggestKey(suggestion) {
    if (!suggestion) {
        return 'suggest:unknown';
    }
    return `${suggestion.type || '?'}:${suggestion.id || '?'}`;
}
